import { CollapseProps } from '@chakra-ui/react';
import { AnimatePresence, motion, Transition, Variants } from 'framer-motion';
import { forwardRef, type MutableRefObject, useCallback } from 'react';
import type { MotionCSS } from 'framer-motion/types/motion/types';
import type { ResolvedValues } from 'framer-motion/types/render/types';
import { setQDrawerGutterWidth } from '../../QOrganisms';
import { QFlex } from '../../QLayouts';

export type QCollapseHorizontalProps = Pick<
  CollapseProps,
  'unmountOnExit' | 'children'
> &
  MotionCSS & {
    isOpen: boolean;
    isDrawerGutter?: boolean;
  };

/**
 * QHorizontalCollapse animates its width to show or hide
 * its children.
 *
 * It is a fork of Chakra's Collapse component, changed
 * to work horizontally rather than vertically.
 */
export const QCollapseHorizontal = forwardRef<
  HTMLDivElement,
  QCollapseHorizontalProps
>(
  (
    {
      isOpen,
      unmountOnExit,
      isDrawerGutter,
      children,
      height = '100%',
      ...motionCSS
    },
    ref,
  ) => {
    const custom = {
      startingWidth: 0,
      endingWidth: 'auto',
      transitionEnd: {
        exit: unmountOnExit
          ? undefined
          : {
              display: 'none',
            },
      },
    };

    const show = unmountOnExit ? isOpen : true;
    const animate = isOpen || unmountOnExit ? 'enter' : 'exit';

    const onUpdate = useCallback(
      isDrawerGutter
        ? (latest: ResolvedValues) => {
            if (isNaN(Number(latest.width))) {
              const el = (ref as MutableRefObject<HTMLDivElement | null>)
                ?.current;
              const w = el?.getBoundingClientRect().width;
              if (w) {
                setQDrawerGutterWidth(w);
              }
            } else {
              setQDrawerGutterWidth(Number(latest.width));
            }
          }
        : () => {
            /* intentional no-op */
          },
      [ref],
    );

    return (
      <AnimatePresence
        initial={false}
        custom={custom}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({} as any)}
      >
        {show && (
          <motion.div
            ref={ref}
            className="chakra-collapse"
            style={{
              display: 'block',
              height: height,
              ...motionCSS,
            }}
            custom={custom}
            variants={variants}
            initial={unmountOnExit ? 'exit' : false}
            animate={animate}
            exit="exit"
            transition={transition}
            onUpdate={onUpdate}
          >
            {/* Keep content at its natural width; clip during animation */}
            <QFlex
              width="max-content"
              minWidth="max-content"
              height="full"
              flexShrink={0}
            >
              {children}
            </QFlex>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);

QCollapseHorizontal.displayName = 'QCollapseHorizontal';

const transition: Transition = { ease: 'easeOut', duration: 0.2 };

const variants: Variants = {
  exit: ({ startingWidth, transition, transitionEnd }) => ({
    opacity: 0,
    width: startingWidth,
    transitionEnd: transitionEnd?.exit,
    transition: transition?.exit,
  }),
  enter: ({ endingWidth, transition, transitionEnd }) => ({
    opacity: 1,
    width: endingWidth,
    transitionEnd: transitionEnd?.enter,
    transition: transition?.enter,
  }),
};
